import { useRouter } from 'next/router';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';

import { UNIT_ALERT, type UnitAlertValue } from '../../../../constants/AddPropertyConstants';

import PropertyConstants from '../../../../constants/PropertyConstants';
import { getDashboardOverviewRoute, getListingRoute } from '../../../../constants/urls';
import {
  emitAddressEntryNextEvent,
  emitCreateListingEvent,
  emitSkipDescribeYouQuestionEvent,
  emitSkipNameQuestionEvent,
  emitSkipUnitCountQuestionEvent,
  emitSubmitDescribeYouQuestionEvent,
  emitSubmitNameQuestionEvent,
  emitSubmitUnitCountQuestionEvent,
} from '../../../../analytics/events/userOnboarding';
import { STEPS } from '../types';

import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type {
  GetUserPropertyDetailsResponseWebDto,
  ProfileType,
  PropertyCountRangeType,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { RentalManagerUserProfileWebApiHandler_saveUserInfo_Args } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import type { CreatePropertyFormData } from '../../../../hooks/queries/useCreateProperty';
import type { ListingErrorsReducerProps } from '../../AddPropertyModal/reducers';
import type { StepType } from '../types';

const {
  LISTING_TYPES: { RENTAL, ROOM },
  ERROR_CODE: { IS_MULTI_FAMILY_RESTRICTED },
} = PropertyConstants;

type UseStepHandlersParams = {
  user: ZrmClickstreamUser | null;
  firstName: string;
  lastName: string;
  selectedProfileType?: ProfileType;
  propertyCountRange?: PropertyCountRangeType;
  saveUserInfo: (
    args: RentalManagerUserProfileWebApiHandler_saveUserInfo_Args,
    options?: { onSuccess?: () => void; onError?: (error: unknown) => void },
  ) => void;
  listing: {
    isRoomForRent?: boolean | string;
    propertyType?: string;
    address?: string;
    unit?: string;
    numUnits?: string;
    customerId?: string;
    duplicateAccountListingAlias?: string | null;
  };
  setListingErrors: (action: ListingErrorsReducerProps) => void;
  verifyCanSubmitForm: (options: { isPromptUnitNumberCheckRequired: boolean }) => Promise<boolean>;
  createProperty: (params: CreatePropertyFormData) => Promise<GetUserPropertyDetailsResponseWebDto>;
  setIsSubmitDisabled: (value: boolean) => void;
  unitAlert: UnitAlertValue | null;
  setUnitAlert: (alert: UnitAlertValue | null) => void;
  setPropertyAlreadyPostedWarningMessage: (value: string | Array<JSX.Element | string>) => void;
  setCurrentStepIndex: (updater: (prev: number) => number) => void;
  propertyAlreadyPostedWarningMessage: string | Array<JSX.Element | string>;
  onSaveSuccess: () => void;
  onClose: () => void;
};

export const useStepHandlers = (params: UseStepHandlersParams) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const handleSkip = (step: StepType, advance: () => void) => {
    if (step === STEPS.NAME) emitSkipNameQuestionEvent({ user: params.user });
    if (step === STEPS.PROFILE_TYPE) emitSkipDescribeYouQuestionEvent({ user: params.user });
    if (step === STEPS.PROPERTY_COUNT) emitSkipUnitCountQuestionEvent({ user: params.user });
    advance();
  };

  const handleProfileStep = (step: StepType, advance: () => void) => {
    let getParams;
    if (step === STEPS.NAME) {
      getParams = { firstName: params.firstName.trim(), lastName: params.lastName.trim() };
    } else if (step === STEPS.PROFILE_TYPE && params.selectedProfileType !== undefined) {
      getParams = { profileType: params.selectedProfileType };
    } else if (step === STEPS.PROPERTY_COUNT && params.propertyCountRange !== undefined) {
      getParams = { propertyCountRangeType: params.propertyCountRange };
    }

    if (getParams === undefined) {
      advance();
      return;
    }

    params.saveUserInfo(
      { getParams },
      {
        onSuccess: () => {
          if (step === STEPS.NAME) {
            emitSubmitNameQuestionEvent({ user: params.user });
            queryClient.setQueryData(['modules', 'auth-react', 'Auth.Provider'], (oldData: typeof params.user) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                firstName: params.firstName.trim(),
                lastName: params.lastName.trim(),
              };
            });
          }
          if (step === STEPS.PROFILE_TYPE && params.selectedProfileType !== undefined)
            emitSubmitDescribeYouQuestionEvent({ user: params.user, profileType: params.selectedProfileType });
          if (step === STEPS.PROPERTY_COUNT && params.propertyCountRange !== undefined)
            emitSubmitUnitCountQuestionEvent({ user: params.user, unitCountRange: params.propertyCountRange });

          params.onSaveSuccess();
        },
        onError: (error: unknown) => {
          logError({ error, context: `useStepHandlers.handleProfileStep - ${step}` });
          advance();
        },
      },
    );
  };

  const handlePropertyDetailsStep = (advance: () => void) => {
    params.setIsSubmitDisabled(true);
    params
      .verifyCanSubmitForm({ isPromptUnitNumberCheckRequired: params.unitAlert !== UNIT_ALERT.PROMPT_FOR_UNIT })
      .then((isValid) => {
        if (isValid) {
          emitAddressEntryNextEvent({ user: params.user });
          advance();
        }
      })
      .finally(() => {
        params.setIsSubmitDisabled(false);
      });

    if (params.unitAlert === UNIT_ALERT.VALIDATED_WITHOUT_UNIT) {
      params.setUnitAlert(null);
    } else if (params.unitAlert === UNIT_ALERT.PROMPT_FOR_UNIT && params.listing.unit) {
      params.setUnitAlert(null);
    }

    if (params.propertyAlreadyPostedWarningMessage) {
      params.setPropertyAlreadyPostedWarningMessage('');
    }
  };

  const handlePropertyConfirmationStep = async () => {
    params.setIsSubmitDisabled(true);
    const { isRoomForRent, propertyType, address, unit, numUnits, customerId, duplicateAccountListingAlias } =
      params.listing;
    const listingType = isRoomForRent ? ROOM : RENTAL;
    const isCommunityType = propertyType === PropertyConstants.PROPERTY_TYPES.COMMUNITY;

    // Duplicate listing exists. Redirect to existing listing rather than creating new one
    if (duplicateAccountListingAlias) {
      router.push(getListingRoute({ listingAlias: duplicateAccountListingAlias }));
      return;
    }

    if (!address || !propertyType) {
      params.setIsSubmitDisabled(false);
      return;
    }

    try {
      const responseData = await params.createProperty({
        listingType,
        propertyType,
        fullAddress: address,
        ...(isCommunityType ? { numUnits } : { unitNumber: unit }),
        ...(listingType !== ROOM && customerId ? { customerId } : {}),
      });
      const { listingAlias } = responseData;
      emitCreateListingEvent({ user: params.user, listing: responseData });
      void queryClient.invalidateQueries({ queryKey: ['getProperties'] });
      if (isCommunityType) {
        router.push(getListingRoute({ listingAlias }));
      } else {
        router.push(getDashboardOverviewRoute({ listingAlias, showSuccessModal: true }));
      }
      params.onClose();
    } catch (err) {
      const isMultiFamilyRestricted = err.code === IS_MULTI_FAMILY_RESTRICTED;
      const fieldWithError = isMultiFamilyRestricted ? 'propertyType' : 'address';
      params.setListingErrors({ type: fieldWithError, payload: err.message });
      params.setCurrentStepIndex((i) => Math.max(0, i - 1));
    } finally {
      params.setIsSubmitDisabled(false);
    }
  };

  return {
    handleSkip,
    handleProfileStep,
    handlePropertyDetailsStep,
    handlePropertyConfirmationStep,
  };
};
